import { ComponentChildren } from "preact"
import { classes } from "@/utils"
import { QuickBooksSyncExplainer } from "@/accounting/quickbooks_sync_explainer"

/**
 * Shared collapsible wrapper for the plain-English accounting explainers (same look as
 * `QuickBooksSyncExplainer`).
 */
function ExplainerSection({
  title,
  className,
  children,
}: {
  title: string
  className?: string
  children: ComponentChildren
}) {
  return (
    <details class={classes("group rounded-lg border border-gray-200 bg-gray-50", className)}>
      <summary class="cursor-pointer select-none px-4 py-3 text-sm font-medium text-gray-700 hover:text-gray-900">
        {title}
        <span class="ml-2 text-xs font-normal text-gray-400 group-open:hidden">(click to expand)</span>
      </summary>
      <div class="space-y-3 border-t border-gray-200 px-4 py-4 text-sm text-gray-600">{children}</div>
    </details>
  )
}

function Step({ label, children }: { label: string; children: ComponentChildren }) {
  return (
    <li>
      <span class="font-medium text-gray-700">{label}</span> {children}
    </li>
  )
}

/**
 * Explains exactly what pressing "Apply to {system}" writes to the connected accounting system, in
 * the order the server performs the writes. Content mirrors the provider tails in
 * `server/src/accounting/cash_application.rs` (`apply_quickbooks` / `apply_netsuite` /
 * `apply_sage_intacct` / `apply_xero`) — keep the two in sync when the apply flow changes.
 */
export function ApplyExplainer({
  system,
  per_deduction,
  className,
}: {
  system: string
  /** Xero per-deduction credit-note mode: one credit note per deduction instead of one grouped memo. */
  per_deduction?: boolean
  className?: string
}) {
  return (
    <ExplainerSection title={`What does “Apply to ${system}” do?`} className={className}>
      <p>
        This page is a preview — nothing has been written to {system} yet. Pressing{" "}
        <span class="font-medium">Apply to {system}</span> creates these records in {system}, in order:
      </p>
      {system === "NetSuite" ? (
        <ol class="list-decimal space-y-2 pl-5">
          <Step label="Credit memo.">
            One credit memo for the selected customer with a line per GL account shown above, posted through the
            NetSuite item mapped to that account.
          </Step>
          <Step label="Duplicate check.">
            Before creating the payment, MarginWiz looks for an existing NetSuite customer payment with this check
            number. If one already exists for the same total it is reused instead of creating a duplicate; if the
            totals differ, the apply stops with an error so you can resolve it in NetSuite.
          </Step>
          <Step label="Customer payment.">
            One customer payment for the check amount on your chosen payment date, deposited to the selected account
            (falling back to the NetSuite integration&apos;s default deposit account). The payment applies the cash
            and the credit memo across the matched invoices, and posts immediately.
          </Step>
          <Step label="MarginWiz.">
            The check is marked applied here and linked to the NetSuite payment.
          </Step>
        </ol>
      ) : system === "Sage Intacct" ? (
        <ol class="list-decimal space-y-2 pl-5">
          <Step label="Credit memo (AR adjustment).">
            One AR adjustment for the selected customer with a line per GL account shown above.
          </Step>
          <Step label="AR payment.">
            One AR payment for the check amount on your chosen payment date, deposited to the selected account. The
            payment applies the cash and the adjustment across the matched invoices.
          </Step>
          <Step label="Submit.">
            If Sage creates the payment in draft state, MarginWiz submits it so it posts to the General Ledger.
          </Step>
          <Step label="MarginWiz.">
            The check is marked applied here and linked to the Sage Intacct payment.
          </Step>
        </ol>
      ) : system === "Xero" ? (
        <ol class="list-decimal space-y-2 pl-5">
          <Step label="Validation.">
            Every GL account on the credit memo must have a valid account Code in Xero. If any doesn&apos;t, the
            apply stops before anything is written.
          </Step>
          <Step label="Credit note.">
            {per_deduction
              ? "One accounts-receivable credit note per deduction, each dated with its own execution date and referencing its invoice."
              : "One accounts-receivable credit note for the selected customer with a line per GL account shown above."}
          </Step>
          <Step label="Allocation.">
            The credit {per_deduction ? "notes are" : "note is"} allocated to the matched invoices{" "}
            {per_deduction ? "they offset" : "it offsets"}.
          </Step>
          <Step label="Payments.">
            One payment per matched invoice for its cash portion (invoice total minus the credit applied to it), on
            your chosen payment date, deposited to the selected bank account — Xero requires one to record payments.
          </Step>
          <Step label="MarginWiz.">
            The check is marked applied here and linked to the Xero payments.
          </Step>
        </ol>
      ) : (
        <ol class="list-decimal space-y-2 pl-5">
          <Step label="Credit memo.">
            One credit memo for the selected customer with a line per GL account + item shown above, posted through
            the item mapped to that account. If a GL account has no mapped item, the apply stops and
            MarginWiz offers to create the missing items for you.
          </Step>
          <Step label="Payment.">
            One customer payment for the check amount (referencing the check number) on your chosen payment date,
            deposited to the selected account. The payment links every matched invoice, paying each with its cash
            portion and applying the credit memo to cover the rest.
          </Step>
          <Step label="MarginWiz.">
            The check is marked applied here and linked to the QuickBooks payment.
          </Step>
        </ol>
      )}
      <p>
        If a later step fails, the records already created are kept in {system} and pressing Apply again resumes
        where it left off — the credit memo is never created twice.
      </p>
    </ExplainerSection>
  )
}

/**
 * Collapsible, plain-English explanation of exactly what the NetSuite sync does, in the order the
 * sync job runs. Content mirrors `server/src/integration/netsuite/job.rs` — keep the two in sync
 * when the job changes.
 */
export function NetSuiteSyncExplainer({ className }: { className?: string }) {
  return (
    <ExplainerSection title="How does the NetSuite sync work?" className={className}>
      <p>
        The sync <span class="font-medium">reads</span> from NetSuite — it never creates or changes anything in
        NetSuite on its own. Records are only written to NetSuite when you explicitly publish a credit memo or
        confirm a cash application. Each sync runs these steps in order:
      </p>
      <ol class="list-decimal space-y-2 pl-5">
        <Step label="Chart of Accounts → Ledger Accounts.">
          Every NetSuite GL account is matched to a MarginWiz ledger account (by NetSuite id, then account number)
          and its name, number, and type are refreshed. Accounts that don&apos;t exist here yet are added, hidden
          from deduction matching by default.
        </Step>
        <Step label="Items.">
          All active NetSuite items are fetched. When an item points at a GL account whose ledger account has no item
          mapping yet, the mapping is filled in automatically (existing mappings are never overwritten). NetSuite
          credit memo lines are item-based, so a GL account needs a mapped item before a credit memo can be published
          against it.
        </Step>
        <Step label="Invoices.">
          All NetSuite invoices are synced, including open balances. These are what deductions get matched against
          and what payments get applied to during cash application.
        </Step>
        <Step label="Payments (checks).">
          All NetSuite customer payments are synced and auto-matched to MarginWiz checks by check number and amount,
          so checks that were already applied in NetSuite show as applied here.
        </Step>
        <Step label="Customers.">
          The customer picker on cash application is filled from the customers appearing on synced transactions.
        </Step>
        <Step label="Credit memos.">
          Existing NetSuite credit memos are synced and auto-matched to checks, so deduction write-offs done directly
          in NetSuite are recognized.
        </Step>
      </ol>
      <p>
        The sync runs when you press <span class="font-medium">Sync</span> and on your organization&apos;s
        configured schedule.
      </p>
    </ExplainerSection>
  )
}

/**
 * Collapsible, plain-English explanation of exactly what the Sage Intacct sync does, in the order
 * the sync job runs. Content mirrors `server/src/integration/sage_intacct/job.rs` — keep the two in
 * sync when the job changes.
 */
export function SageIntacctSyncExplainer({ className }: { className?: string }) {
  return (
    <ExplainerSection title="How does the Sage Intacct sync work?" className={className}>
      <p>
        The sync <span class="font-medium">reads</span> from Sage Intacct — it never creates or changes anything in
        Sage on its own. Records are only written to Sage when you explicitly publish a credit memo or confirm a
        cash application. Each sync runs these steps in order:
      </p>
      <ol class="list-decimal space-y-2 pl-5">
        <Step label="GL accounts → Ledger Accounts.">
          Every Sage GL account is matched to a MarginWiz ledger account and its name and number are refreshed.
        </Step>
        <Step label="AR invoices.">
          Invoices are synced incrementally since the last sync (with a one-day buffer to catch late entries),
          including open balances. These are what deductions get matched against and what payments get applied to
          during cash application.
        </Step>
        <Step label="AR payments (checks).">
          Customer payments are synced and auto-matched to MarginWiz checks by check number and amount, so checks
          that were already applied in Sage show as applied here.
        </Step>
        <Step label="Customers.">
          The customer picker on cash application is filled from the customers appearing on synced transactions.
        </Step>
        <Step label="Deductions from the GL (optional).">
          When deduction GL accounts are configured on the integration, posted journal activity on those accounts is
          imported as deductions.
        </Step>
      </ol>
      <p>
        The sync runs when you press <span class="font-medium">Sync</span> and on your organization&apos;s
        configured schedule.
      </p>
    </ExplainerSection>
  )
}

/**
 * Collapsible, plain-English explanation of exactly what the Xero sync does, in the order the sync
 * job runs. Content mirrors `server/src/integration/xero/mod.rs` — keep the two in sync when the
 * job changes.
 */
export function XeroSyncExplainer({ className }: { className?: string }) {
  return (
    <ExplainerSection title="How does the Xero sync work?" className={className}>
      <p>
        The sync <span class="font-medium">reads</span> from Xero — it never creates or changes anything in Xero on
        its own. Records are only written to Xero when you explicitly publish a credit note or confirm a cash
        application. Each sync runs these steps in order:
      </p>
      <ol class="list-decimal space-y-2 pl-5">
        <Step label="Chart of Accounts → Ledger Accounts.">
          Every Xero account is matched to a MarginWiz ledger account (by Xero account id, then Code, then name) and
          refreshed. Accounts that don&apos;t exist here yet are added, hidden from deduction matching by default.
          Credit note lines reference accounts by their Code, so accounts without a Code in Xero can&apos;t be used
          for cash application.
        </Step>
        <Step label="Invoices.">
          All Xero invoices are synced, including open balances and line items. These are what deductions get
          matched against and what payments get applied to during cash application.
        </Step>
        <Step label="Payments (checks).">
          Customer payments are synced and auto-matched to MarginWiz checks by check number and amount, so checks
          that were already applied in Xero show as applied here.
        </Step>
        <Step label="Customers.">
          The customer picker on cash application is filled from the customers appearing on synced transactions.
        </Step>
      </ol>
      <p>
        A Xero connection with multiple organizations must have one selected (Settings → Connections) before it can
        sync. The sync runs when you press <span class="font-medium">Sync</span> and on your organization&apos;s
        configured schedule.
      </p>
    </ExplainerSection>
  )
}

/** Renders the sync explainer for the connected accounting system (`accounting_system` label). */
export function AccountingSyncExplainer({ system, className }: { system: string; className?: string }) {
  switch (system) {
    case "NetSuite":
      return <NetSuiteSyncExplainer className={className} />
    case "Sage Intacct":
      return <SageIntacctSyncExplainer className={className} />
    case "Xero":
      return <XeroSyncExplainer className={className} />
    default:
      return <QuickBooksSyncExplainer className={className} />
  }
}
